/**
 * Curator Bridge Slice-4 error typing.
 *
 * A curator decision is consequential and idempotency-keyed, so "it failed" is
 * not a usable answer: whether the same decision_key may be sent again depends
 * entirely on WHICH failure happened. This module is the one place that decides.
 *
 * Ground truth is the installed @supabase/supabase-js client. `invoke()` never
 * throws; it resolves `{ data, error }` and the error is one of three classes:
 *
 *   FunctionsFetchError  the fetch itself rejected - DNS, refused connection,
 *                        CORS, abort. No response was ever seen.
 *   FunctionsRelayError  Supabase's relay answered with x-relay-error, meaning
 *                        it could not reach the function. The function did not
 *                        run.
 *   FunctionsHttpError   the function answered with a non-2xx status. Its
 *                        `context` is the Response.
 *
 * A fourth case is local: withSupabaseTimeout races invoke against a 15s timer
 * and does NOT pass an AbortSignal, so when the timer wins it throws
 * SupabaseTimeoutError while the request stays in flight. The decision may still
 * commit afterwards. That is precisely why a timeout is retryable with the SAME
 * key rather than a new one.
 *
 * Anything else is `unknown` and NOT retryable. Classifying an arbitrary
 * TypeError as a network failure would invite a retry loop over a programming
 * bug, and would let a defect masquerade as a transient outage.
 */

import {
  FunctionsFetchError,
  FunctionsHttpError,
  FunctionsRelayError,
} from '@supabase/supabase-js';
import { SupabaseTimeoutError } from '@/auth/session-guard';

export type CuratorErrorCategory =
  /** No usable response: fetch rejected, or we stopped waiting for one. */
  | 'network'
  /** Supabase's relay could not reach the function; it never ran. */
  | 'relay'
  /** The function answered, with a non-2xx status. */
  | 'server_http'
  /** Anything else, including programming errors. Never retried. */
  | 'unknown';

export interface CuratorApiErrorInit {
  category: CuratorErrorCategory;
  httpStatus: number | null;
  serverCode: string | null;
  serverMessage: string | null;
  retryable: boolean;
  message: string;
  cause?: unknown;
}

/**
 * A real Error subclass, so it survives `throw`, React Query's error channel and
 * `instanceof` without being reduced to a plain object or a string.
 */
export class CuratorApiError extends Error {
  readonly category: CuratorErrorCategory;
  /** The HTTP status when a response was actually received; otherwise null. */
  readonly httpStatus: number | null;
  /** `error.code` from the server's { error: { code, message } } envelope. */
  readonly serverCode: string | null;
  /** `error.message` from the same envelope, verbatim. */
  readonly serverMessage: string | null;
  /** Whether resending the IDENTICAL request (same decision_key) is sound. */
  readonly retryable: boolean;

  constructor(init: CuratorApiErrorInit) {
    super(init.message);
    this.name = 'CuratorApiError';
    this.category = init.category;
    this.httpStatus = init.httpStatus;
    this.serverCode = init.serverCode;
    this.serverMessage = init.serverMessage;
    this.retryable = init.retryable;
    if (init.cause !== undefined) this.cause = init.cause;
  }

  /**
   * A conflict means the request was well-formed but disagrees with the ledger's
   * current truth - a stale state or version, an illegal transition, or a key
   * already spent on a different intent. Re-reading is mandatory before another
   * attempt, so this is deliberately separate from `retryable`.
   */
  get isConflict(): boolean {
    return this.category === 'server_http' && this.httpStatus === 409;
  }

  get isAuthFailure(): boolean {
    return (
      this.category === 'server_http' &&
      (this.httpStatus === 401 || this.httpStatus === 403)
    );
  }

  get isNotFound(): boolean {
    return this.category === 'server_http' && this.httpStatus === 404;
  }
}

/**
 * Whether an HTTP status may be retried with the same decision_key.
 *
 * 5xx is retryable and this is not a guess. In `handleDecision` the success-path
 * audit write is awaited WITHOUT a try/catch, so a decision can commit in the
 * database and still surface as a 500 when the later audit fails. Refusing to
 * retry would strand that decision; retrying is safe because the server replays
 * the same key idempotently rather than deciding twice.
 *
 * 409 is never retryable as-is: the ledger has moved and the expected state and
 * version must be re-read first. 400/401/403/404 are not retryable either -
 * resending an identical request reproduces them exactly.
 */
export function isRetryableHttpStatus(status: number): boolean {
  if (status === 409) return false;
  return status >= 500;
}

interface ParsedErrorBody {
  code: string | null;
  message: string | null;
}

/**
 * Reads the structured envelope produced by supabase/functions/_shared/errors.ts:
 * `{ error: { code, message, details? } }`.
 *
 * The body is consumed EXACTLY once. A Response body is a one-shot stream, so a
 * second `.json()` on the same Response throws; this is called on each Response
 * once and the Response is never retained afterwards.
 */
async function parseErrorBody(response: Response): Promise<ParsedErrorBody> {
  try {
    const body: unknown = await response.json();
    const envelope =
      body !== null && typeof body === 'object' && !Array.isArray(body)
        ? (body as { error?: unknown })
        : null;
    const inner =
      envelope?.error !== null &&
      typeof envelope?.error === 'object' &&
      !Array.isArray(envelope?.error)
        ? (envelope.error as { code?: unknown; message?: unknown })
        : null;
    return {
      code: typeof inner?.code === 'string' ? inner.code : null,
      message: typeof inner?.message === 'string' ? inner.message : null,
    };
  } catch {
    // Not JSON, already consumed, or an empty body. The status still classifies
    // the failure, so this is a missing detail rather than a second failure.
    return { code: null, message: null };
  }
}

function responseOf(error: { context?: unknown }): Response | null {
  return error.context instanceof Response ? error.context : null;
}

const TIMEOUT_MESSAGE =
  'The request timed out before an answer arrived. It may still have been applied — retrying is safe.';
const NETWORK_MESSAGE =
  'Could not reach the server. The request may not have been sent — retrying is safe.';
const RELAY_MESSAGE =
  'The curation service could not be reached, so nothing was recorded. Retrying is safe.';

/** Human-readable fallback when the server sent no usable message. */
function statusMessage(status: number): string {
  if (status === 409) {
    return 'This signal changed since it was read. Refresh the queue and decide again.';
  }
  if (status === 401) return 'Your session has expired. Sign in again to continue.';
  if (status === 403) return 'Signal curation requires the curator capability.';
  if (status === 404) return 'That signal is no longer available.';
  if (status >= 500) return `The server could not complete the request (${status}).`;
  return `The request was refused (${status}).`;
}

/**
 * Normalizes any thrown or returned failure into exactly one CuratorApiError.
 *
 * Idempotent: passing a CuratorApiError back returns it unchanged, so a value
 * that has already been classified is never reclassified (and its Response,
 * already consumed, is never read a second time).
 */
export async function toCuratorApiError(error: unknown): Promise<CuratorApiError> {
  if (error instanceof CuratorApiError) return error;

  // Local timeout: we stopped waiting, but the request was never aborted.
  if (error instanceof SupabaseTimeoutError) {
    return new CuratorApiError({
      category: 'network',
      httpStatus: null,
      serverCode: null,
      serverMessage: null,
      retryable: true,
      message: TIMEOUT_MESSAGE,
      cause: error,
    });
  }

  if (error instanceof FunctionsHttpError) {
    const response = responseOf(error);
    if (response) {
      const status = response.status;
      const body = await parseErrorBody(response);
      return new CuratorApiError({
        category: 'server_http',
        httpStatus: status,
        serverCode: body.code,
        serverMessage: body.message,
        retryable: isRetryableHttpStatus(status),
        message: body.message ?? statusMessage(status),
        cause: error,
      });
    }
    // A FunctionsHttpError without a Response cannot be classified by status.
    return new CuratorApiError({
      category: 'unknown',
      httpStatus: null,
      serverCode: null,
      serverMessage: null,
      retryable: false,
      message: error.message,
      cause: error,
    });
  }

  if (error instanceof FunctionsRelayError) {
    // The relay never delivered the request, so no decision was recorded and
    // the same key may be sent again. The status is recorded when present, but
    // the category stays `relay`: this is not the function's own answer.
    return new CuratorApiError({
      category: 'relay',
      httpStatus: responseOf(error)?.status ?? null,
      serverCode: null,
      serverMessage: null,
      retryable: true,
      message: RELAY_MESSAGE,
      cause: error,
    });
  }

  if (error instanceof FunctionsFetchError) {
    return new CuratorApiError({
      category: 'network',
      httpStatus: null,
      serverCode: null,
      serverMessage: null,
      retryable: true,
      message: NETWORK_MESSAGE,
      cause: error,
    });
  }

  // Everything else - a ReauthRequiredError, a TypeError from our own code, a
  // rejected string. Not retryable: a defect must not be re-sent as though it
  // were weather.
  return new CuratorApiError({
    category: 'unknown',
    httpStatus: null,
    serverCode: null,
    serverMessage: null,
    retryable: false,
    message: error instanceof Error ? error.message : String(error),
    cause: error,
  });
}
